import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  model,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DeleteIconComponent } from '@kuiper/icons';
import { TableEmptyStateComponent } from '../../../../../../../ui-components/lib/table/table-empty-state/table-empty-state.component';
import { AvatarComponent } from '../../../../../../../ui-components/lib/avatar/avatar.component';
import { LoaderComponent } from '../../../../../../../ui-components/lib/loader/loader.component';
import { ModalServiceService } from '../../../../../../../ui-components/lib/modal/modal-service.service';
import { TruncatePipe } from '../../../../../../../ui-components/pipes/truncate/truncate.pipe';
import { ActionListComponent } from '../../../../../../common/components/action-list/action-list.component';
import { FeatureFlagsService } from '../../../../../../common/services/feature-flags/feature-flags.service';
import { TenantService } from '../../../../../../common/services/tenant/tenant.service';
import { ActionList } from '../../../../../../common/models/action-list.model';
import { FEATURE_FLAGS } from '../../../../../../common/constants/feature-flags.constants';

@Component({
  selector: 'lib-workflow-details-table-view',
  imports: [
    AvatarComponent,
    TranslocoPipe,
    ActionListComponent,
    TruncatePipe,
    LoaderComponent,
    TableEmptyStateComponent,
    NgTemplateOutlet,
    DeleteIconComponent,
  ],
  templateUrl: './workflow-details-table-view.component.html',
  styleUrl: './workflow-details-table-view.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WorkflowDetailsTableViewComponent {
  router = inject(Router);
  removeUserModal = viewChild<TemplateRef<unknown>>('removeUser');
  modalService = inject(ModalServiceService);
  transloco = inject(TranslocoService);
  tenantService = inject(TenantService);
  featureFlagsService = inject(FeatureFlagsService);

  readonly isScheduleWorkflowToRunOnceEnabled = signal(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.schedule_a_workflow_to_run_once
    )
  );

  tableData = input<any[]>([]);
  workflowId = model.required<string>();
  currentPage = input.required<number>();
  currentSize = input.required<number>();
  memberEmitter = output<any>();
  removingUser = input<boolean>(false);
  selectedMember = signal<string>('');
  isLoading = input<boolean>(false);

  actionList: ActionList[] = [
    {
      key: 'edit',
      hasIcon: true,
      label: this.transloco.translate(
        'workflow_permission_details_screen_action_item_edit_label'
      ),
      hasDivider: false,
      labelCustomStyle: '',
      hasToggle: false,
      toggleValue: false,
    },
    {
      key: 'remove',
      hasIcon: true,
      label: this.transloco.translate(
        'workflow_permission_details_screen_action_item_remove_label'
      ),
      hasDivider: false,
      labelCustomStyle: 'text-destructive-500 font-medium text-xs',
      hasToggle: false,
      toggleValue: false,
    },
  ] as ActionList[];

  itemClick(event: any, member: any) {
    this.selectedMember.set(member);
    if (event == 'edit') {
      this.router.navigateByUrl(
        this.tenantService.adminPath(
          `workflow/permissions/permission-details/${this.workflowId()}/edit/${member.id}`
        )
      );
      this.memberEmitterAction('edit', this.selectedMember());
    } else {
      this.openModal();
    }
  }

  memberEmitterAction(action: string, selectedMember: any) {
    this.memberEmitter.emit({ action, selectedMember });
  }

  handlePermissions(permission: string, permissionArray: any[]) {
    const mappedArray = permissionArray.map(item => item.action);
    return mappedArray.includes('own')
      ? true
      : mappedArray.includes(permission);
  }

  isOwner(permissionArray: any[]) {
    return permissionArray.some(ele => ele.action == 'own');
  }

  openModal() {
    const modal = {
      isOpen: true,
      hasCloseIcon: false,
      customizeContent: this.removeUserModal(),
      width: '24',
    };

    this.modalService.modalContent.set(modal);
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }
}
